/**
 * Phase 2 — OCR-Pipeline.
 *
 * Phase ist kind-aware (Migration 034 + Diäten-Fork Design 2026-05-19):
 *  - card.kind = "timesheet"        → Stundenliste-OCR-Inspektor (8 Steps).
 *                                     Production-pfad cell_dispatch.process_timesheet_from_pdf
 *                                     mit Per-Cell-Schema (data.days[N].field.{ocr,corrections}).
 *  - card.kind = "expense"|"unknown" → Diäten-OCR-Inspektor (8 Steps).
 *                                     Production-pfad heute legacy
 *                                     paddle_vl.process_expense (entries[] mit slim Cells).
 *                                     Per-Cell-Schema-Migration für expense ist nächste Phase.
 *
 * Beide Pfade teilen sich:
 *  - Step 1 (PDF rendern)
 *  - Step 3 (Pre-Deskew Pass 0)
 *  - Step 6 (Header-OCR)
 *  - Step 8 (Conflict-Pre-Check)
 *
 * Fork: Step 2 (Form-Typ + Routing), 4 (Grid für 5- vs 9-Spalten), 5 (Per-Cell mit
 * verschiedenen Prompts), 7 (Symbol vs. boolean/datum/freitext).
 */
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import type { WerkstattCardContext } from "../../pages/WerkstattPage";
import { api } from "../../lib/api";
import { SettingsTestOcr, PipelineStepList, type PipelineStep } from "../SettingsTestOcr";
import { PhaseHeader, withUuids } from "./Phase1Upload";
import { stepUuid } from "../../lib/stepUuid";
import { computeJourney } from "../../lib/rooms";
import { RunInspector } from "./RunInspector";
import { QueueDashboard } from "./QueueDashboard";
import { ConflictResolver } from "./ConflictResolver";
import { HighlightAutoTest } from "./HighlightAutoTest";
import { StuckOcrBanner } from "./StuckOcrBanner";
import { useQueueAvgRuntime } from "../../lib/ocrProgress";

export function Phase2Ocr({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card;

  // Kind-aware PhaseHeader: outputs + prodFiles unterscheiden sich.
  const isExpense = c?.kind === "expense" || c?.kind === "unknown";

  return (
    <div>
      <PhaseHeader
        num={2}
        title={isExpense ? "OCR (Diäten-Pfad)" : "OCR (Stundenliste-Pfad)"}
        purpose={
          isExpense
            ? "Pixel → strukturierte Reise-Einträge. Eine Cell-Klasse pro Spaltentyp "
              + "(Zeit / Datum / Zahl / Boolean / Freitext)."
            : "Pixel → strukturierte Tages-Daten. K3-Strategie mit Recovery-Chain bei Grid-FN."
        }
        inputs="PDF-Bytes (von Phase 1)"
        outputs={
          isExpense
            ? "data.entries[i].{tag,datum,kd_nr,kunde,zm_abzug,m_verr,ort,abfahrt,rueckkehr} + data.header"
            : "data.days{1..31}.{von1,bis1,von2,bis2,symb} + data.header + employee_id (auto-resolved)"
        }
        prodFiles={
          isExpense
            ? [
                "ocr/pipeline_core.py (geshared)",
                "ocr/engines/paddle_vl.py:process_expense (Legacy-VLM-Path)",
                "ocr/engines/cell_dispatch.py:process_expense_from_pdf — geplant (Per-Cell-Schema Migration)",
                "worker/src/handlers/ocr.ts",
              ]
            : [
                "ocr/pipeline_core.py",
                "ocr/engines/cell_dispatch.py",
                "ocr/engines/_ocr_dispatch.py",
                "worker/src/handlers/ocr.ts",
                "frontend/src/components/PdfWithHighlights.tsx",
                "frontend/src/components/werkstatt/HighlightAutoTest.tsx",
                "hub/src/routes/test-protocols.ts",
              ]
        }
        userGates="Keine — vollautomatisch. Bei Fehlschlag → pending_review"
      />

      {c && <StuckOcrBanner card={c} onRestart={ctx.reload} />}

      {c ? (
        isExpense ? <ExpenseOcrInspector ctx={ctx} /> : <TimesheetOcrInspector ctx={ctx} />
      ) : (
        <div>
          <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
            Keine aktive Karte. Generischer OCR-Test zum Experimentieren mit beliebigen PDFs:
          </div>
          <div className="phase-embedded-tool">
            <SettingsTestOcr />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stundenliste-Pfad ─────────────────────────────────────────────────────

function TimesheetOcrInspector({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card!;
  const t = ctx.timesheet;
  const avgRuntimeS = useQueueAvgRuntime();
  const data = (t?.data as any) ?? {};
  const days = data.days ?? {};
  const header = data.header ?? null;
  const daysFilled = Object.keys(days).filter(k => /^\d+$/.test(k)).length;
  const pipelineMeta = (data.meta?.pipeline as Record<string, any> | undefined) ?? {};
  const preDeskewInput: number | null | undefined = pipelineMeta.pre_deskew_input_deg;
  const preDeskewApplied: number | null | undefined = pipelineMeta.pre_deskew_applied_deg;
  const loc = useLocation();

  let cellsTotal = 0, cellsLowConf = 0;
  for (const k of Object.keys(days)) {
    if (!/^\d+$/.test(k)) continue;
    const d = days[k];
    for (const f of ["von1", "bis1", "von2", "bis2", "symb"]) {
      const cell = d?.[f];
      if (cell && typeof cell === "object" && cell.value) {
        cellsTotal++;
        if (typeof cell.confidence === "number" && cell.confidence < 0.85) {
          cellsLowConf++;
        }
      }
    }
  }

  const ocrRunning = c.state === "ocr_running";
  const ocrDone = daysFilled > 0 || ["ocr_complete", "validated", "calculated", "exported", "pending_conflict"].includes(c.state);
  const resolved = (data as any).conflict_resolved as { action?: string; resolved_at?: string } | undefined;

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "PDF rendern (220 DPI)",
      description: "ocr/pipeline_core.py: PyMuPDF → BGR-Image. Resolution ist die Stellschraube für OCR-Qualität.",
      status: ocrDone ? "done" : ocrRunning ? "active" : "pending",
      detail: ocrDone ? "Image-Snapshot in /app/processed/<id>.processed.png abgelegt" : "(läuft)",
    },
    {
      num: 2,
      title: "Form-Typ detektieren → Stundenliste-Route",
      description: "Tesseract liest Top-Region in 4 Rotationen, sucht Stundenliste/Diäten-Hints. "
        + "Hits=stundenliste → diese Karte. Hits=diaeten würde in den Expense-Pfad gehen.",
      status: ocrDone ? "done" : "pending",
      detail: ocrDone ? "form_type = stundenliste ✓" : "(wartet)",
    },
    {
      num: 3,
      title: "Pre-Deskew (Pass 0)",
      description:
        "ocr/pipeline_core.py: Hough-Tilt-Messung über lange horizontale Tabellenlinien "
        + "(np.pi/3600 ≈ 0.05° Auflösung). |angle| ≥ 0.3° → Image wird CCW rotiert, "
        + "downstream-PNG (processed/<id>.png), Cell-Bboxes und PdfWithHighlights-Overlay "
        + "sehen alle dasselbe begradigte Bild.",
      status: ocrDone ? "done" : ocrRunning ? "active" : "pending",
      detail: !ocrDone
        ? "(wartet)"
        : preDeskewApplied != null
          ? `↻ Tilt ${(preDeskewInput ?? preDeskewApplied).toFixed(2)}° korrigiert`
          : preDeskewInput != null
            ? `kein Tilt — θ=${preDeskewInput.toFixed(2)}° unter Schwelle 0.30°`
            : pipelineMeta.pre_deskew_input_deg === undefined
              ? "(historisch — diese Karte wurde vor Pass-0 OCR'd)"
              : "Tilt nicht messbar (zu wenig Hough-Linien)",
    },
    {
      num: 4,
      title: "Grid-Detection 31×5 (4-Pass Recovery) — Highlights erstellt",
      description:
        "ocr/pipeline_core.py: Primary → Deskew → CLAHE → Aggressive. Erste erfolgreiche "
        + "Strategie gewinnt. Output: cell_bboxes_keyed = 155 Cells (31 Tage × 5 Felder), "
        + "die Highlight-Overlays die PdfWithHighlights rendert.",
      status: ocrDone ? "done" : "pending",
      detail: !ocrDone ? "(wartet)" : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ display: "inline-flex", gap: 12, alignItems: "baseline" }}>
            cell_bboxes_keyed populated · recovery-strategy persisted in audit
            {" · "}
            <Link
              to={`/timesheets/${c.id}`}
              state={{ background: loc }}
              style={{ fontWeight: 600 }}
              title="Öffnet die kanonische Validierungs-UI als Modal über dem Werkstatt-Step"
            >
              🔍 PDF-Highlight-Audit öffnen →
            </Link>
          </span>
          <HighlightAutoTest cardId={c.id} />
        </div>
      ),
    },
    {
      num: 5,
      title: "Per-Cell OCR — Zeit (qwen3_vl, force-time-v3)",
      description:
        "qwen3_vl pro Time-Cell (von1/bis1/von2/bis2) mit Force-Time-Prompt + Confidence-"
        + "Filter ≥ 0.85. K3-Strategie. Schreibt data.days[N].field.{ocr,value,...} mit "
        + "vollem Per-Cell-Provenance (Modell + Prompt-Version + raw_response).",
      status: ocrDone ? "done" : ocrRunning ? "active" : "pending",
      detail: (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span>
            {ocrDone
              ? `${cellsTotal} Cells gelesen · ${cellsLowConf} mit Confidence < 0.85`
              : ocrRunning ? "läuft (Qwen3-VL ~5-10 min CPU)…" : "(wartet)"}
          </span>
          <PerCellModelTest cardId={c.id} kind="timesheet" />
        </div>
      ),
    },
    {
      num: 6,
      title: "Header-OCR + Employee-Auto-Match",
      description: "qwen3_header_text + Levenshtein gegen employees. employee_id wird gesetzt wenn eindeutig.",
      status: ocrDone ? (c.employee_id ? "done" : "error") : "pending",
      detail: ocrDone
        ? c.employee_id
          ? `auto-matched: ${ctx.employee?.last_name ?? ""}, ${ctx.employee?.first_name ?? ""} (${header?.employee_raw ?? "?"})`
          : `header_raw=${header?.employee_raw ?? "?"} aber kein eindeutiger Match`
        : "(wartet)",
    },
    {
      num: 7,
      title: "Symbol-OCR — qwen3_symb (K/U/F/ZA/…)",
      description: "qwen3_symb closed-list classifier (K=Krank, U=Urlaub, ZA=Zeitausgleich, F=Feiertag, …). Setzt symb-Wert pro Tag mit Confidence.",
      status: ocrDone ? "done" : "pending",
      detail: ocrDone ? `${daysFilled}/31 Tage befüllt` : "(wartet)",
    },
    {
      num: 8,
      title: "Conflict-Pre-Check + State-Transition",
      description:
        "Worker prüft VOR dem state-Write ob employee+month bereits eine andere "
        + "aktive Karte hat. Bei Konflikt direkt state=pending_conflict.",
      status: !ocrDone ? "pending"
            : c.state === "pending_conflict" ? "error"
            : "done",
      detail: c.state === "pending_conflict" ? (
        <>
          🔴 Konflikt detektiert — Resolver unten oder{" "}
          <Link to={`/werkstatt/abrechnung/konflikt?card=${c.id}`}>in Phase 7 öffnen →</Link>
        </>
      ) : resolved?.action ? (
        <>
          🟡 Konflikt war erkannt → in{" "}
          <Link to={`/werkstatt/abrechnung/konflikt?card=${c.id}`}>Phase 7</Link>{" "}
          mit „<strong>{resolved.action}</strong>" aufgelöst
          {resolved.resolved_at && ` (${new Date(resolved.resolved_at).toLocaleString("de-AT")})`}
          {" "}→ state=<code>{c.state}</code>
        </>
      ) : (
        <>state = <code>{c.state}</code></>
      ),
    },
  ];

  // Queue-Dashboard + RunInspector sind nur sinnvoll während die Karte aktiv
  // im pg-boss-Queue ist (state ocr_pending/ocr_running). Bei ocr_complete+
  // ist das Rauschen — 20 Zeilen-Tabelle anderer Jobs ohne Bezug zur Karte.
  const queueActive = c.state === "ocr_pending" || c.state === "ocr_running";

  return (
    <>
      {queueActive && (
        <>
          <QueueDashboard highlightCardId={c.id} />
          <RunInspector card={c} avgRuntimeS={avgRuntimeS} />
        </>
      )}
      <PipelineStepList steps={withUuids(c.id, "ocr", steps)} />
      <ConflictResolver card={c} timesheet={t} employees={ctx.employees} onResolved={ctx.reload} />
    </>
  );
}

/**
 * Per-Cell Model-Test — inline button in Step 5 (Per-Cell OCR).
 *
 * Analog zu HighlightAutoTest in Step 4: ein Trigger-Button + bei Klick
 * embedded SettingsTestOcr im `fromCard`-Modus. Lädt die Karte ins
 * Per-Cell-Test-UI: klickbare Zellen, beliebige Models nebeneinander
 * vergleichbar.
 *
 * Production-Single-Source: Code-Pfad ist identisch zum /admin/test-pipeline
 * Standalone-Tool — Step 5 zeigt nur die existing Pipeline transparent.
 * (Added 2026-05-20 nach „v4 results insufficient"-Feedback — Per-Cell-
 * Feedback-Loop für Model-Wahl.)
 */
function PerCellModelTest({ cardId, kind }: { cardId: string; kind: "timesheet" | "expense" }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <PerCellTestRunHistory cardId={cardId} />
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          padding: "5px 12px", fontSize: 12, fontWeight: 600,
          background: open ? "#6b7280" : "#1d4ed8",
          color: "white", border: 0, borderRadius: 4, cursor: "pointer",
          alignSelf: "flex-start",
        }}
        title="Öffnet das Per-Cell Model-Vergleich-UI für diese Karte: klickbare Zellen, beliebige Models nebeneinander."
      >
        {open ? "🔬 Per-Cell Test schließen" : "🔬 Per-Cell Model-Test öffnen"}
      </button>
      {open && (
        <div style={{ marginTop: 4, padding: 12, border: "1px solid #d6e3ff", borderRadius: 4, background: "#f5f9ff" }}>
          <SettingsTestOcr fromCard={{ id: cardId, kind }} />
        </div>
      )}
    </div>
  );
}

/** Werkstatt-Visibility (2026-05-21): Liste der letzten Per-Cell-Test-Runs
 *  für die aktuelle Karte. Zeigt duration_s + cell-totals + GT-source so dass
 *  Iterations-Vergleich (Baseline → Fix → Re-Test) direkt am Card-Werkstatt-
 *  Link nachvollziehbar ist. */
function PerCellTestRunHistory({ cardId }: { cardId: string }) {
  const [runs, setRuns] = useState<Array<{
    test_id: string; created_at: string; pipeline_status: string;
    result: any; reads_count: number; gt_count: number;
  }> | null>(null);

  useEffect(() => {
    let alive = true;
    api.admin.testOcrByCard(cardId, 5).then(r => {
      if (alive) setRuns(r.runs);
    }).catch(() => {});
    return () => { alive = false; };
  }, [cardId]);

  if (!runs || runs.length === 0) return null;

  return (
    <details style={{ fontSize: 12, marginBottom: 4 }}>
      <summary style={{ cursor: "pointer", fontWeight: 600, color: "#374151" }}>
        📊 Per-Cell-Test-Runs ({runs.length})
      </summary>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginTop: 6 }}>
        <thead>
          <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
            <th style={{ padding: "4px 8px" }}>Wann</th>
            <th style={{ padding: "4px 8px" }}>Test-ID</th>
            <th style={{ padding: "4px 8px", textAlign: "right" }}>Dauer</th>
            <th style={{ padding: "4px 8px", textAlign: "right" }}>Cells</th>
            <th style={{ padding: "4px 8px", textAlign: "right" }}>GT</th>
            <th style={{ padding: "4px 8px" }}>Modelle</th>
            <th style={{ padding: "4px 8px" }}>GT-Quelle</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(r => {
            const batch = r.result?.batch_run;
            const dur = batch?.duration_s;
            const models = batch?.models_used as string[] | undefined;
            const totals = batch?.totals;
            const gtSrc = batch?.gt_source_card_id as string | undefined;
            return (
              <tr key={r.test_id} style={{ borderTop: "1px solid #e5e7eb" }}>
                <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{new Date(r.created_at).toLocaleString("de-AT")}</td>
                <td style={{ padding: "4px 8px", fontFamily: "monospace", fontSize: 11 }}>{r.test_id.slice(0, 8)}</td>
                <td style={{ padding: "4px 8px", textAlign: "right" }}>{dur ? `${dur.toFixed(1)}s` : "—"}</td>
                <td style={{ padding: "4px 8px", textAlign: "right" }}>{totals?.cells_with_value ?? "—"}/{totals?.cells_total ?? r.reads_count}</td>
                <td style={{ padding: "4px 8px", textAlign: "right" }}>{r.gt_count || "—"}</td>
                <td style={{ padding: "4px 8px", fontFamily: "monospace", fontSize: 11 }}>{models?.join(", ") ?? "—"}</td>
                <td style={{ padding: "4px 8px", fontFamily: "monospace", fontSize: 11 }}>{gtSrc ? gtSrc.slice(0, 8) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </details>
  );
}

// ─── Diäten-Pfad ───────────────────────────────────────────────────────────

/**
 * Diäten OCR-Raum Drill-in — „einen Raum tiefer" im OCR-Schritt.
 *
 * Führt die ECHTE Extraktion (hub → sidecar /ocr/expense → paddle_vl.process_expense)
 * gegen das Karten-PDF LIVE aus und zeigt entries + meta + Dauer, OHNE die Karte zu
 * verändern (committing:false). Derselbe Code-Pfad, den der Worker beim echten Upload
 * fährt — nur seiteneffektfrei zum Beobachten/Debuggen. Ersetzt den Stundenlisten-
 * Test-Harness (der für Diäten WRONG_FORM_TYPE liefert). Vergleicht die Live-Posten
 * gegen die committeten (Abweichung sichtbar = Drift zwischen Pfad und gespeichertem Stand).
 */
function ExpenseExtractDebug({ cardId, committedCount }: { cardId: string; committedCount: number }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [res, setRes] = useState<Awaited<ReturnType<typeof api.cards.debugExtract>> | null>(null);

  const run = async () => {
    setBusy(true); setErr(null);
    try { setRes(await api.cards.debugExtract(cardId)); }
    catch (e: any) { setErr(e?.message ?? String(e)); }
    finally { setBusy(false); }
  };

  const valOf = (cell: any) => (cell == null ? "" : typeof cell === "string" ? cell : (cell.value ?? ""));
  const delta = res ? res.entries.length - committedCount : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ alignSelf: "flex-start", padding: "5px 12px", fontSize: 12, fontWeight: 600,
          background: open ? "#6b7280" : "#1d4ed8", color: "white", border: 0, borderRadius: 4, cursor: "pointer" }}
        title="Führt die echte Diäten-Extraktion live aus (seiteneffektfrei) — der reale Worker-Pfad zum Beobachten."
      >
        {open ? "🔬 Re-Extraktion schließen" : "🔬 Re-Extraktion (Debug, seiteneffektfrei)"}
      </button>
      {open && (
        <div style={{ padding: 12, border: "1px solid #d6e3ff", borderRadius: 4, background: "#f5f9ff" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={run} disabled={busy}
              style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600,
                background: busy ? "#9ca3af" : "#059669", color: "white", border: 0, borderRadius: 4, cursor: "pointer" }}>
              {busy ? "extrahiert…" : "↻ Jetzt live extrahieren"}
            </button>
            <span style={{ fontSize: 11, color: "#6b7280" }}>
              ruft hub → sidecar <code>/ocr/expense</code> (paddle_vl) · schreibt NICHTS in die Karte
            </span>
          </div>
          {err && <div style={{ color: "#dc2626", fontSize: 12 }}>Fehler: {err}</div>}
          {res && (
            <div style={{ fontSize: 12 }}>
              <div style={{ marginBottom: 6, color: "#374151" }}>
                Live: <strong>{res.entries.length}</strong> Posten in {res.elapsed_ms} ms
                {res.meta?.row_count != null && <> · aus {res.meta.row_count} Tabellenzeilen</>}
                {" · "}committet: <strong>{committedCount}</strong>
                {delta === 0
                  ? <span style={{ color: "#059669" }}> ✓ identisch</span>
                  : <span style={{ color: "#b45309" }}> ⚠ Abweichung ({delta > 0 ? "+" : ""}{delta})</span>}
              </div>
              {res.entries.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: "#eef2ff", textAlign: "left" }}>
                      <th style={{ padding: "3px 6px" }}>#</th>
                      <th style={{ padding: "3px 6px" }}>Datum</th>
                      <th style={{ padding: "3px 6px" }}>Kunde/Ort</th>
                      <th style={{ padding: "3px 6px" }}>Abfahrt</th>
                      <th style={{ padding: "3px 6px" }}>Rückkehr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.entries.map((e, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "3px 6px" }}>{i + 1}</td>
                        <td style={{ padding: "3px 6px" }}>{valOf(e.datum) || valOf(e.tag) || "—"}</td>
                        <td style={{ padding: "3px 6px" }}>{[valOf(e.kunde), valOf(e.ort)].filter(Boolean).join(" · ") || "—"}</td>
                        <td style={{ padding: "3px 6px" }}>{valOf(e.abfahrt) || "—"}</td>
                        <td style={{ padding: "3px 6px" }}>{valOf(e.rueckkehr) || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {res.meta && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: "pointer", color: "#6b7280" }}>meta (roh)</summary>
                  <pre style={{ fontSize: 10, overflow: "auto", background: "#f9fafb", padding: 8, borderRadius: 4 }}>
                    {JSON.stringify(res.meta, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExpenseOcrInspector({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card!;
  const loc = useLocation();
  // Diäten-Karten landen in `expenses`-Tabelle. timesheet-Property von ctx ist
  // null. Wir lesen `data` aus der card direkt (Card-Interface ist union).
  const data = (c as any).data ?? {};
  const entries: any[] = Array.isArray(data.entries) ? data.entries : [];
  // Räumliches Modell (PR #40): homography-projizierte Geometrie aus dem
  // Spatial-Prepass — Basis der pixelgenauen Highlights + des AutoTests.
  const spatialMeta = (data.meta?.spatial as Record<string, any> | undefined) ?? null;

  // Per-Eintrag-Evidenz als verschachtelte Sub-Schritte (der „nächste Raum"
  // der Extraktion) — gelesen aus der KARTE selbst (data.entries), nicht aus
  // einem losen globalen Harness. Jeder Eintrag ist adressierbar (stepUuid).
  const valOf = (cell: any) => (cell == null ? "" : typeof cell === "string" ? cell : (cell.value ?? ""));
  const entryChildren: PipelineStep[] = entries.map((e, i): PipelineStep => {
    const datum = valOf(e.datum), kunde = valOf(e.kunde), ort = valOf(e.ort);
    const abf = valOf(e.abfahrt), rueck = valOf(e.rueckkehr);
    const hasTime = !!abf && !!rueck;
    return {
      num: i + 1,
      title: `Eintrag ${i + 1}${datum ? ` — ${datum}` : ""}`,
      description: [kunde, ort].filter(Boolean).join(" · ") || "(kein Kunde/Ort erkannt)",
      status: datum && hasTime ? "done" : (datum || hasTime ? "active" : "pending"),
      detail: `Abfahrt ${abf || "—"} → Rückkehr ${rueck || "—"}`,
      uuid: stepUuid(c.id, "ocr", `extract.entry.${i}`),
    };
  });

  // Steps = die Artefakt-Journey (Single Source: rooms.ts). Jeder Schritt = Raum
  // mit Eingangskriterium (needs) → Ausgang (produces); Status aus der Twin-Mappe.
  // PipelineStepList propagiert einen Stop (kein Grün dahinter). Der Stundenlisten-
  // Test-Harness ist NICHT Teil dieser Sequenz (andere Pipeline) — er steht unten
  // als getrenntes Diagnose-Werkzeug.
  const calc = data.calculation?.totals;
  const detailFor: Record<string, string> = {
    upload: `${c.pdf_orig_filename ?? "—"} · kind=${c.kind}`,
    ocr: entries.length > 0
      ? `${entries.length} Posten extrahiert${data.meta?.row_count ? ` (aus ${data.meta.row_count} Tabellenzeilen)` : ""}`
      : "noch keine Posten extrahiert",
    validierung: c.employee_id
      ? `Mitarbeiter: ${(ctx.employee?.last_name ?? "")} ${(ctx.employee?.first_name ?? "")}`.trim()
      : "Mitarbeiter noch nicht zugeordnet",
    berechnung: calc ? `Tagessatz ${calc.tagessatz_total ?? 0} € · Inland-Tage ${calc.inland_days ?? 0}` : "—",
    review: "—",
    export: c.state === "exported" ? "RZL-CSV erstellt" : "—",
  };
  const STATUS_MAP: Record<string, PipelineStep["status"]> = {
    done: "done", active: "active", error: "error", "not-reached": "pending",
  };
  const steps: PipelineStep[] = computeJourney(c as any, "expense").map((r, i): PipelineStep => ({
    num: i + 1,
    title: r.label,
    description: `Eingang: ${r.needs.length ? r.needs.join(", ") : "—"} → Ausgang: ${r.produces}`,
    status: STATUS_MAP[r.status] ?? "pending",
    detail: r.status === "not-reached"
      ? `⚪ nicht erreicht — ${r.reason}`
      : r.id === "ocr"
        ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Grid-Detection (Template-Homography) — Highlights erstellt.
                  Analog zu Stundenliste Step 4: Status der räumlichen Geometrie
                  + Audit-Link + automatischer Highlight-Validator (Gridlinien-
                  Truth) dessen Verdict persistiert wird (Run-ID referenzierbar). */}
              <div style={{
                border: "1px solid #e5e7eb", borderRadius: 4, padding: "8px 10px",
                background: "#fafafa", display: "flex", flexDirection: "column", gap: 6,
              }}>
                <strong style={{ fontSize: 12 }}>
                  Grid-Detection (Template-Homography) — Highlights erstellt
                </strong>
                <span style={{ fontSize: 12 }}>
                  {spatialMeta
                    ? <>meta.spatial ✓ — Scan um {spatialMeta.rotation_applied_cw ?? 0}° aufgerichtet · {spatialMeta.inliers ?? "?"} Inliers · {(spatialMeta.row_boundaries_pct?.length ?? 1) - 1} Zeilen / {(spatialMeta.col_lines_pct?.length ?? 1) - 1} Spalten projiziert</>
                    : "meta.spatial fehlt — Karte wurde vor dem Spatial-Prepass OCR'd (Re-OCR erzeugt es) → Highlights laufen auf statischer Heuristik"}
                  {" · "}
                  <Link
                    to={`/expenses/${c.id}`}
                    state={{ background: loc }}
                    style={{ fontWeight: 600 }}
                    title="Öffnet die kanonische Diäten-UI als Modal — Markierung am ganzen PDF prüfen (Human Quality Gate)"
                  >
                    🔍 PDF-Highlight-Audit öffnen →
                  </Link>
                </span>
                <HighlightAutoTest cardId={c.id} />
              </div>
              <span>{detailFor.ocr}</span>
              {/* Drill-in: „einen Raum tiefer" — echte Extraktion live, seiteneffektfrei */}
              <ExpenseExtractDebug cardId={c.id} committedCount={entries.length} />
            </div>
          )
        : detailFor[r.id],
    children: r.id === "ocr" && entryChildren.length ? entryChildren : undefined,
    blockedReason: r.status === "error" ? r.reason : undefined,
  }));

  return (
    <>
      <div style={{
        background: "#ecfdf5", border: "1px solid #10b981", padding: "8px 12px",
        borderRadius: 4, marginBottom: 12, fontSize: 13,
      }}>
        <strong>🧾 Diäten-Pipeline</strong> — Upload-Routing (manuelle Typ-Wahl), Tabellen-Extraktion
        (paddle_vl, Zeilen→Posten) und Tages-Aggregation (Tagessatz) laufen produktiv. Die Schritte
        unten sind als sequenzielle Pipeline verschachtelt: jeder Schritt zeigt seine Evidenz als
        aufklappbaren Sub-Raum. Extraktion = Diäten-spezifisch; Render/Deskew/Header/Conflict mit dem
        Stundenliste-Pfad geshared.
      </div>
      {/* QueueDashboard + RunInspector sind pg-boss-zentriert — Diäten-Karten
          laufen heute via paddle_vl Legacy-Path OHNE card.ocr-Queue.
          Anzeige bringt nur Rauschen, wird im Diäten-Body weggelassen. */}
      <div style={{ marginBottom: 16 }}>
        <Link
          to={`/expenses/${c.id}`}
          state={{ background: loc }}
          style={{
            display: "inline-block", padding: "6px 12px",
            background: "#1d4ed8", color: "white", borderRadius: 4,
            textDecoration: "none", fontSize: 13, fontWeight: 600,
          }}
          title="Öffnet die kanonische Diäten-Validierungs-UI als Modal — zeigt PDF-Crop + Reclassify-Buttons"
        >
          🔍 Diäten-Detail-Audit öffnen →
        </Link>
      </div>
      <PipelineStepList steps={withUuids(c.id, "ocr", steps)} />
      {/* Der frühere „Stundenlisten-Test-Harness (separat)" ist entfernt: er fuhr die
          falsche Pipeline (WRONG_FORM_TYPE für Diäten). An seine Stelle tritt der echte
          Diäten-Pfad-Debug, integriert in den OCR-Raum oben (ExpenseExtractDebug). */}
    </>
  );
}
